import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "../ui/button";
import CartButton from "./CartButton";
import { getSessionProfileId } from "../../context/OnboardingContext";

export default function Navbar({ onCTAClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setHasSession(!!getSessionProfileId());
  }, [location.pathname]);

  const isAdmin = location.pathname.startsWith("/admin");
  const isShop = location.pathname.startsWith("/shop");
  const isHerzkammer = location.pathname.startsWith("/lovezone") || location.pathname.startsWith("/heartcore") || location.pathname.startsWith("/herzkammer");

  const isPreview = location.pathname === "/preview";
  const anchorPrefix = isPreview ? "" : "/preview";
  const links = [
    { label: "Wie es funktioniert", href: `${anchorPrefix}#how`, isHash: true },
    { label: "Sicherheit", href: `${anchorPrefix}#safety`, isHash: true },
    { label: "Warum QHearts", href: `${anchorPrefix}#benefits`, isHash: true },
    { label: "Events", href: `${anchorPrefix}#events`, isHash: true },
    { label: "LoveZone", href: "/lovezone", isHash: false },
    { label: "Love-Stories", href: `${anchorPrefix}#testimonials`, isHash: true },
    { label: "Shop", href: "/shop", isHash: false },
    { label: "Profil", href: hasSession ? "/profile" : "/login", isHash: false },
  ];

  const renderCtaButton = () => {
    if (isAdmin) {
      return (
        <Link to="/">
          <Button
            variant="ghost"
            className="rounded-full text-sm h-10 px-4"
            data-testid="nav-back-home"
          >
            Zur Webseite
          </Button>
        </Link>
      );
    }
    if (hasSession) {
      return (
        <Link to="/profile">
          <Button
            className="rounded-full bg-qh-rose hover:bg-qh-rosedark text-white px-5 h-10 text-sm font-medium transition-all hover:-translate-y-0.5 whitespace-nowrap"
            data-testid="nav-cta-profile"
          >
            Mein Profil
          </Button>
        </Link>
      );
    }
    return (
      <Link to="/onboarding">
        <Button
          className="rounded-full bg-qh-rose hover:bg-qh-rosedark text-white px-5 h-10 text-sm font-medium transition-all hover:-translate-y-0.5 whitespace-nowrap"
          data-testid="nav-cta-register"
        >
          Registrieren
        </Button>
      </Link>
    );
  };

  return (
    <nav
      data-testid="nav-bar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || isShop || isAdmin
          ? "bg-white/80 backdrop-blur-xl border-b border-qh-line"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
          <img
            src="/assets/qhearts-logo.png"
            alt="QHearts"
            className="h-14 sm:h-16 w-auto object-contain select-none"
            draggable="false"
          />
        </Link>

        {!isAdmin && (
          <div className="hidden xl:flex items-center gap-x-5 2xl:gap-x-6">
            {links.map((l) => {
              const isActive =
                (l.href === "/shop" && isShop) ||
                (l.href === "/lovezone" && isHerzkammer) ||
                (l.label === "Profil" &&
                  (location.pathname === "/profile" ||
                    location.pathname === "/login"));
              return (
                <Link
                  key={l.label}
                  to={l.href}
                  className={`text-[13px] 2xl:text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-qh-rose"
                      : "text-qh-slate hover:text-qh-ink"
                  }`}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              );
            })}
          </div>
        )}

        <div className="hidden xl:flex items-center gap-2.5 shrink-0">
          <CartButton className="h-10 w-10" />
          {renderCtaButton()}
        </div>

        <div className="flex xl:hidden items-center gap-2">
          <CartButton className="h-10 w-10" />
          <button
            className="h-10 w-10 rounded-full border border-qh-line bg-white flex items-center justify-center text-qh-ink"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && !isAdmin && (
        <div
          className="xl:hidden border-t border-qh-line bg-white"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.href}
                onClick={() => setOpen(false)}
                className="py-2 text-qh-ink font-medium"
              >
                {l.label}
              </Link>
            ))}
            <Button
              onClick={() => {
                setOpen(false);
                if (hasSession) navigate("/profile");
                else navigate("/onboarding");
              }}
              className="rounded-full bg-qh-rose hover:bg-qh-rosedark text-white mt-2"
              data-testid="nav-mobile-cta-register"
            >
              {hasSession ? "Mein Profil" : "Registrieren"}
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
